import { createContext, useContext, useEffect, useState } from 'react';
import { supabase, supabaseAdmin } from '../lib/supabase';
import OrganizationFeatureService from '../services/OrganizationFeatureService';

// Simple auth cache for instant loading
const authCache = {
  STORAGE_KEY: 'karate_auth_cache',
  EXPIRY_HOURS: 24,

  set(authData) {
    try {
      const cacheData = {
        data: authData,
        timestamp: Date.now(),
        expires: Date.now() + (this.EXPIRY_HOURS * 60 * 60 * 1000)
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('Failed to cache auth data:', error);
      return false;
    }
  },

  get() {
    try {
      const cached = localStorage.getItem(this.STORAGE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);

      // Check if cache has expired
      if (Date.now() > cacheData.expires) {
        this.clear();
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.error('Failed to retrieve cached auth data:', error);
      this.clear();
      return null;
    }
  },

  clear() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear cache:', error);
      return false;
    }
  }
};

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizationFeatures, setOrganizationFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [usingCachedData, setUsingCachedData] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Check if we're in password reset mode - if so, don't initialize normal auth
        const isPasswordResetMode = sessionStorage.getItem('isPasswordResetMode') === 'true';
        const resetSessionStart = sessionStorage.getItem('resetSessionStart');

        // If in password reset mode, check if it's been too long (expire after 10 minutes)
        if (isPasswordResetMode && resetSessionStart) {
          const resetAge = Date.now() - parseInt(resetSessionStart);
          const maxResetAge = 10 * 60 * 1000; // 10 minutes

          if (resetAge > maxResetAge) {
            // Reset session expired, clear it and sign out
            sessionStorage.removeItem('isPasswordResetMode');
            sessionStorage.removeItem('resetSessionStart');
            await supabase.auth.signOut();
          } else {
            // Still in valid password reset mode, don't initialize normal auth
            setAuthInitialized(true);
            setLoading(false);
            return;
          }
        }

        // First, try to load from cache for instant startup
        const cachedAuth = authCache.get();
        if (cachedAuth && cachedAuth.user) {
          setUser(cachedAuth.user);
          setProfile(cachedAuth.profile);
          setOrganization(cachedAuth.organization);
          setOrganizationFeatures(cachedAuth.organizationFeatures);
          setAuthInitialized(true);
          setLoading(false);
          setUsingCachedData(true);

          // Start background refresh to ensure data is current
          setTimeout(() => {
            if (isMounted) {
              refreshAuthDataInBackground();
            }
          }, 1000); // Give UI time to render with cached data

          return; // Exit early with cached data
        }

        // No cache or expired cache - show loading and fetch fresh

        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setUser(session?.user ?? null);
        setAuthInitialized(true);

        if (session?.user) {
          await fetchUserData(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error during auth initialization:', error);
        if (isMounted) {
          setAuthInitialized(true);
          setLoading(false);
        }
      }
    };

    // Start auth initialization
    initializeAuth();

    // Set a timeout fallback to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 5000); // Reduced to 5 seconds

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        setUser(session?.user ?? null);

        if (session?.user) {
          // Check if we already have cached data for this user
          const cachedAuth = authCache.get();
          if (cachedAuth &&
            cachedAuth.user?.id === session.user.id &&
            cachedAuth.profile &&
            !usingCachedData) {
            // We have valid cached data for this user, use it instead of fetching
            setProfile(cachedAuth.profile);
            setOrganization(cachedAuth.organization);
            setOrganizationFeatures(cachedAuth.organizationFeatures);
            setUsingCachedData(true);
            setLoading(false);
          } else if (!profile || profile?.id !== session.user.id) {
            // Only fetch user data if we don't have it or it's for a different user
            await fetchUserData(session.user.id);
          } else {
            // We already have the data for this user, just stop loading
            setLoading(false);
          }
        } else {
          // Clear profile and organization when no session
          setProfile(null);
          setOrganization(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async (userId) => {
    try {
      // Add a timeout for the database query (generous for production)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database query timeout')), 20000);
      });

      // Try a simpler query first - just the profile
      let queryPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const { data: profileData, error: profileError } = await Promise.race([
        queryPromise,
        timeoutPromise
      ]);

      if (profileError) {
        // If it's a "not found" error, check for pending user invitation
        if (profileError.code === 'PGRST116') {
          // Look for an unclaimed invitation with this user's email
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser?.email) {
            try {
              // TEMPORARILY DISABLED: user_invitations table access
              // const { data: invitation, error: invitationError } = await supabase
              //   .from('user_invitations')
              //   .select('*')
              //   .eq('email', currentUser.email)
              //   .eq('status', 'pending')
              //   .single();

              const invitation = null;
              const invitationError = null;

              if (invitation && !invitationError) {
                // Check if invitation is still valid (not expired)
                const now = new Date();
                const expiresAt = new Date(invitation.expires_at);

                if (now < expiresAt) {
                  // Extract data from metadata if available
                  const metadata = invitation.metadata || {};

                  // Create new profile with authenticated user's ID using invitation data
                  const { data: linkedProfile, error: linkError } = await supabase
                    .from('profiles')
                    .insert([{
                      id: userId,
                      email: invitation.email,
                      full_name: invitation.full_name,
                      role: invitation.role,
                      organization_id: invitation.organization_id,
                      center_id: metadata.center_id || null,
                      phone: metadata.phone || '',
                      is_active: true
                    }])
                    .select(`
                      *,
                      organization:organizations(*)
                    `)
                    .single();

                  if (!linkError && linkedProfile) {
                    // TEMPORARILY DISABLED: Mark invitation as accepted
                    // const { error: claimError } = await supabase
                    //   .from('user_invitations')
                    //   .update({
                    //     status: 'accepted',
                    //     accepted_at: new Date().toISOString()
                    //   })
                    //   .eq('id', invitation.id);

                    // if (claimError) {
                    //   console.error('Error marking invitation as accepted:', claimError);
                    // }

                    setProfile(linkedProfile);
                    setOrganization(linkedProfile.organization);
                    setLoading(false);
                    return;
                  }
                } else {
                  console.error('Invitation has expired');
                }
              }
            } catch (invitationCheckError) {
              console.warn('Could not check for user invitations (table may not be accessible):', invitationCheckError);
              // Continue without invitation check
            }
          }

          // No pending invitation found, proceed as new user without profile
          setProfile(null);
          setOrganization(null);
          setLoading(false);
          return;
        }
        throw profileError;
      }

      setProfile(profileData);

      // Fetch organization data separately if the user has an organization_id
      let organizationData = null;
      if (profileData.organization_id) {
        try {
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', profileData.organization_id)
            .single();

          if (!orgError) {
            organizationData = orgData;
          }
        } catch (orgError) {
          console.warn('Could not fetch organization data:', orgError);
        }
      }

      setOrganization(organizationData);

      // Fetch organization features if organization exists
      let organizationFeaturesData = null;
      if (organizationData?.id) {
        try {
          organizationFeaturesData = await OrganizationFeatureService.getOrganizationFeatures(organizationData.id);
          setOrganizationFeatures(organizationFeaturesData);
        } catch (featuresError) {
          console.warn('Could not fetch organization features:', featuresError);
        }
      }

      // Save successful auth data to cache
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        authCache.set({
          user: currentUser,
          profile: profileData,
          organization: organizationData,
          organizationFeatures: organizationFeaturesData
        });
      }

      // Mark that we're no longer using cached data (now have fresh data)
      setUsingCachedData(false);
    } catch (error) {
      if (error.message === 'Database query timeout') {
        console.warn('Database query timed out, checking cache for fallback data');

        // Try to use cached data as fallback
        const cachedData = authCache.get();
        if (cachedData && cachedData.user?.id === userId) {

          setProfile(cachedData.profile);
          setOrganization(cachedData.organization);
          setOrganizationFeatures(cachedData.organizationFeatures);
          setUsingCachedData(true);
          return;
        }

        // If no cache available, show warning but don't block the app
        console.warn('No cached data available after timeout. User can continue with limited functionality.');
        setProfile(null);
        setOrganization(null);
        setOrganizationFeatures(null);
      } else {
        console.error('Error fetching user data:', error);
        // For any other error, still allow the user to proceed
        setProfile(null);
        setOrganization(null);
        setOrganizationFeatures(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, userData) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;

    // Don't create profile here - will be done after organization selection
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    try {
      // Try to sign out normally first
      const { error } = await supabase.auth.signOut();
      if (error) {
        // If it's just a session missing error, that's fine - we're already signed out
        if (error.message?.includes('Auth session missing') || error.message?.includes('session_missing')) {
          console.log('Session already expired - clearing local state');
        } else {
          // For other errors, we still want to know about them
          console.error('Sign out error:', error);
          throw error;
        }
      }
    } catch (error) {
      // If sign out fails due to missing session, just clear local state
      if (error.message?.includes('Auth session missing') || error.message?.includes('session_missing')) {
        console.log('Session already expired during sign out - clearing local state');
      } else {
        // Re-throw other errors
        throw error;
      }
    } finally {
      // Always clear cached auth data and local state on logout attempt
      authCache.clear();
      setUsingCachedData(false);
      setUser(null);
      setProfile(null);
      setOrganization(null);
      setOrganizationFeatures(null);
    }
  };

  const resetPassword = async (email) => {
    // Use production URL if available, otherwise fall back to current origin
    const baseUrl = process.env.REACT_APP_PRODUCTION_URL || window.location.origin;

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${baseUrl}/reset-password`,
    });

    if (error) throw error;
    return data;
  };

  const createOrganization = async (orgData, targetUserId = null) => {
    // Enhanced organization data with fields matching your database schema
    const organizationData = {
      name: orgData.name,
      slug: orgData.slug || generateOrganizationSlug(orgData.name),
      description: orgData.description || '',
      logo_url: orgData.logo_url || null,
      banner_url: orgData.banner_url || null,
      primary_color: orgData.primary_color || '#3B82F6',
      secondary_color: orgData.secondary_color || '#1F2937',
      contact_email: orgData.contact_email || '',
      contact_phone: orgData.contact_phone || '',
      address: orgData.address || '',
      website: orgData.website || '',
      status: orgData.is_trial ? 'trial' : 'active',
      is_active: orgData.is_active !== false, // default to true unless explicitly false
      is_trial: orgData.is_trial || false,
      subscription_plan: orgData.is_trial ? 'trial' : (orgData.subscription_plan || 'basic'),
      max_centers: orgData.max_centers || '1',
      max_students: orgData.max_students || '50',
      features: orgData.features || { payments: true, messaging: true, attendance: true },
      trial_ends_at: orgData.is_trial ?
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : // 30 days from now
        null
    };

    const { data: orgResult, error: orgError } = await supabase
      .from('organizations')
      .insert(organizationData)
      .select()
      .single();

    if (orgError) {
      console.error('AuthContext: Organization creation failed:', orgError);
      throw orgError;
    }

    // For now, Super Admin creates organization without immediately creating the owner profile
    // The owner will need to sign up and join the organization using the organization code
    if (isSuperAdmin()) {
      // Just return the organization - owner will sign up later
      return { organization: orgResult, profile: null };
    }

    // Create profile for organization owner (normal user creating their own org)
    const profileData = {
      id: user.id,
      organization_id: orgResult.id,
      email: user.email,
      role: 'organization_owner', // Always organization_owner for the creator
      first_name: orgData.owner_first_name,
      last_name: orgData.owner_last_name,
      phone: orgData.owner_phone
    };

    const { data: profileResult, error: profileError } = await supabase
      .from('profiles')
      .insert(profileData)
      .select()
      .single();

    if (profileError) {
      console.error('AuthContext: Profile creation failed:', profileError);
      throw profileError;
    }

    // Only set organization and profile if this is the current user
    if (!isSuperAdmin()) {
      setOrganization(orgResult);
      setProfile(profileResult);
    }

    const result = { organization: orgResult, profile: profileResult };

    return result;
  };


  const generateOrganizationSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50); // Keep it reasonable length
  };

  // Super Admin functions
  const createOrganizationAsSuper = async (orgData) => {
    if (!isSuperAdmin()) throw new Error('Unauthorized: Super Admin required');

    // Create organization and owner account
    return await createOrganization(orgData, null);
  };

  const getAllOrganizations = async () => {
    if (!isSuperAdmin()) throw new Error('Unauthorized: Super Admin required');

    const { data, error } = await supabase
      .from('organizations')
      .select(`
        *,
        profiles!inner(
          id,
          email,
          first_name,
          last_name,
          role,
          created_at
        )
      `)
      .eq('profiles.role', 'organization_owner')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  };

  const updateOrganizationAsSuper = async (orgId, updates) => {
    if (!isSuperAdmin()) throw new Error('Unauthorized: Super Admin required');

    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', orgId)
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const deleteOrganization = async (orgId) => {
    if (!isSuperAdmin()) throw new Error('Unauthorized: Super Admin required');

    // This should cascade delete all related data due to foreign key constraints
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', orgId);

    if (error) throw error;
  };

  const impersonateOrganization = async (orgId) => {
    if (!isSuperAdmin()) throw new Error('Unauthorized: Super Admin required');

    // Fetch organization data for impersonation
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', orgId)
      .single();

    if (orgError) throw orgError;

    // Temporarily set organization context (but keep super admin profile)
    setOrganization(orgData);

    return orgData;
  };

  const exitImpersonation = () => {
    if (!isSuperAdmin()) throw new Error('Unauthorized: Super Admin required');
    setOrganization(null);
  };

  const joinOrganization = async (organizationId, profileData) => {
    const { data: profileResult, error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        organization_id: organizationId,
        email: user.email,
        ...profileData
      })
      .select()
      .single();

    if (profileError) throw profileError;

    // Fetch organization data
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single();

    if (orgError) throw orgError;

    setOrganization(orgData);
    setProfile(profileResult);

    return { organization: orgData, profile: profileResult };
  };

  const acceptInvitation = async (token, profileData) => {
    // Get invitation details
    const { data: invitation, error: inviteError } = await supabase
      .from('organization_invitations')
      .select('*, organization:organizations(*)')
      .eq('token', token)
      .eq('email', user.email)
      .gt('expires_at', new Date().toISOString())
      .is('accepted_at', null)
      .single();

    if (inviteError) throw inviteError;

    // Create profile
    const { data: profileResult, error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        organization_id: invitation.organization_id,
        email: user.email,
        role: invitation.role,
        ...profileData
      })
      .select()
      .single();

    if (profileError) throw profileError;

    // Mark invitation as accepted
    await supabase
      .from('organization_invitations')
      .update({ accepted_at: new Date().toISOString() })
      .eq('id', invitation.id);

    setOrganization(invitation.organization);
    setProfile(profileResult);

    return { organization: invitation.organization, profile: profileResult };
  };

  const updateProfile = async (updates) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;

    // Update local state and cache
    setProfile(data);
    updateCachedProfile(data);

    return data;
  };

  const updateOrganization = async (updates) => {
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', organization.id)
      .select()
      .single();

    if (error) throw error;

    // Update local state and cache
    setOrganization(data);
    updateCachedOrganization(data);

    return data;
  };

  // Role-based permission checks
  const isSuperAdmin = () => {
    const result = profile?.role === 'super_admin';
    return result;
  };
  const refreshOrganization = async () => {
    if (profile?.organization_id) {
      try {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', profile.organization_id)
          .single();

        if (!orgError && orgData) {
          setOrganization(orgData);
          // Update cache
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser) {
            authCache.set({
              user: currentUser,
              profile: profile,
              organization: orgData
            });
          }
        }
      } catch (error) {
        console.warn('Could not refresh organization data:', error);
      }
    }
  };

  const fixParentOrganization = async () => {
    // This function fixes parent profiles that registered with tokens but are missing organization_id
    if (profile?.role === 'parent' && !profile?.organization_id && profile?.registration_token) {
      try {
        // Look up the token to get the organization_id
        const { data: tokenData, error: tokenError } = await supabase
          .from('center_registration_tokens')
          .select('organization_id, center_id')
          .eq('token', profile.registration_token)
          .single();

        if (!tokenError && tokenData?.organization_id) {
          // Update the profile with the correct organization_id
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              organization_id: tokenData.organization_id,
              center_id: tokenData.center_id // Also set center_id if available
            })
            .eq('id', profile.id);

          if (!updateError) {
            // Update the local profile state
            const updatedProfile = {
              ...profile,
              organization_id: tokenData.organization_id,
              center_id: tokenData.center_id
            };
            setProfile(updatedProfile);

            // Now load the organization data
            const { data: orgData, error: orgError } = await supabase
              .from('organizations')
              .select('*')
              .eq('id', tokenData.organization_id)
              .single();

            if (!orgError && orgData) {
              setOrganization(orgData);
            }

            return true;
          } else {
            console.error('Error updating profile:', updateError);
          }
        } else {
          console.warn('Could not find token or organization_id:', tokenError);
        }
      } catch (error) {
        console.error('Error in fixParentOrganization:', error);
      }
    }
    return false;
  };

  const isOrganizationOwner = () => profile?.role === 'organization_owner';
  const isCenterManager = () => profile?.role === 'center_manager';
  const isCoach = () => profile?.role === 'coach';
  const isParent = () => profile?.role === 'parent';
  const isStudent = () => profile?.role === 'student';


  const getStudentData = async (studentId = null) => {
    if (!user) return null;

    // Don't query if we're still loading or profile is not set
    if (loading || !profile) {

      return null;
    }

    try {
      let query = supabase
        .from('students')
        .select(`
          *,
          parent:profiles!students_parent_id_fkey(
            id, full_name, email, phone
          ),
          enrollments(
            class:classes(
              id, name, day_of_week, start_time, end_time,
              center:centers(name)
            )
          )
        `);

      if (studentId) {
        query = query.eq('id', studentId);
      } else if (isStudent()) {
        // If user is a student with login account, find their student record
        query = query.eq('auth_user_id', user.id);
      } else if (isParent()) {
        // If user is a parent, get all their children
        query = query.eq('parent_id', profile.id);
      } else {
        return null;
      }

      const { data, error } = await query;

      // Handle 406 Not Acceptable errors gracefully
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.warn('Database query not acceptable, possibly due to RLS policies:', error);
          return studentId ? null : [];
        }
        throw error;
      }

      return studentId ? data?.[0] : data;
    } catch (error) {
      console.error('Error fetching student data:', error);
      return null;
    }
  };

  const getParentChildren = async (parentId = null) => {
    if (!user) return [];

    try {
      const parentIdToUse = parentId || (isParent() ? profile.id : null);
      if (!parentIdToUse) return [];

      const { data, error } = await supabase
        .from('students')
        .select(`
          *,
          enrollments(
            class:classes(
              id, name, day_of_week, start_time, end_time,
              center:centers(name)
            )
          )
        `)
        .eq('parent_id', parentIdToUse)
        .eq('organization_id', organization?.id);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching parent children:', error);
      return [];
    }
  };

  const canViewStudent = (studentData) => {
    if (!studentData || !profile) return false;

    // Super admin and org owners can view all students
    if (isSuperAdmin() || isOrganizationOwner()) return true;

    // Parents can view their own children
    if (isParent() && studentData.parent_id === profile.id) return true;

    // Students can view their own data (if they have login account)
    if (isStudent() && studentData.auth_user_id === user?.id) return true;

    // Center managers can view students in their centers
    if (isCenterManager()) {
      // Check if student is enrolled in classes at manager's centers
      const hasEnrollmentInManagedCenter = studentData.enrollments?.some(enrollment => {
        return enrollment.class?.center?.manager_id === profile.id;
      });
      return hasEnrollmentInManagedCenter;
    }

    // Instructors can view students in their classes
    if (isCoach()) {
      // Check if student is enrolled in instructor's classes
      const hasEnrollmentWithCoach = studentData.enrollments?.some(enrollment => {
        return enrollment.class?.coach_assignments?.some(assignment => assignment.coach_id === profile.id);
      });
      return hasEnrollmentWithCoach;
    }

    return false;
  };

  const canEditStudent = (studentData) => {
    if (!studentData || !profile) return false;

    // Super admin and org owners can edit all students
    if (isSuperAdmin() || isOrganizationOwner()) return true;

    // Parents can edit their own children (limited fields)
    if (isParent() && studentData.parent_id === profile.id) return true;

    // Center managers can edit students in their centers
    if (isCenterManager()) {
      const hasEnrollmentInManagedCenter = studentData.enrollments?.some(enrollment => {
        return enrollment.class?.center?.manager_id === profile.id;
      });
      return hasEnrollmentInManagedCenter;
    }

    return false;
  };


  const generateSimplePassword = (name) => {
    // Remove spaces and take first part of name + "1"
    const cleanName = name.replace(/\s+/g, '').toLowerCase();
    return cleanName + '1';
  };

  // Create parent account (complete auth + profile creation)
  const createParentAccount = async (parentData, organizationId) => {
    try {
      const password = generateSimplePassword(parentData.full_name);

      // Step 1: Use admin API to create confirmed user
      if (!supabaseAdmin) {
        throw new Error('Service role key not configured. Please add REACT_APP_SUPABASE_SERVICE_ROLE_KEY to your .env file');
      }

      const { data: authData, error: authError } = await supabaseAdmin.auth.admin.createUser({
        email: parentData.email,
        password: password,
        email_confirm: true, // Auto-confirm the email
        user_metadata: {
          full_name: parentData.full_name,
          role: 'parent',
          organization_id: organizationId
        }
      });

      if (authError) {
        throw new Error(`Account creation failed: ${authError.message}`);
      }

      if (!authData.user) {
        throw new Error('Account creation failed: No user returned');
      }

      // Step 2: Create the profile with the auth user ID
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          full_name: parentData.full_name,
          email: parentData.email,
          phone: parentData.phone || '',
          role: 'parent',
          organization_id: organizationId,
          is_active: true
        })
        .select()
        .single();

      if (profileError) {
        console.error('Profile creation failed:', profileError);
        throw new Error(`Profile creation failed: ${profileError.message}`);
      }

      return {
        success: true,
        profile: profileData,
        password: password,
        accountType: 'parent',
        emailConfirmed: true
      };

    } catch (error) {
      console.error('Parent account creation failed:', error);
      return { success: false, error: error.message };
    }
  };

  // Direct student account creation (like parent accounts)
  const createStudentLoginAccount = async (studentData, organizationId, parentId) => {
    try {
      if (!parentId) {
        throw new Error('Parent ID is required for student account creation');
      }

      const password = generateSimplePassword(studentData.full_name);

      // Create actual Supabase auth account (not just invitation)
      if (!supabaseAdmin) {
        throw new Error('Service role key not configured. Please add REACT_APP_SUPABASE_SERVICE_ROLE_KEY to your .env file');
      }

      const { data: authData, error: authError } = await supabaseAdmin.auth.admin.createUser({
        email: studentData.email,
        password: password,
        email_confirm: true, // Auto-confirm the email
        user_metadata: {
          full_name: studentData.full_name,
          // Don't set role: 'student' to avoid triggering automatic student creation
          organization_id: organizationId,
          parent_id: parentId
        }
      });

      if (authError) {
        throw new Error(`Account creation failed: ${authError.message}`);
      }

      if (!authData.user) {
        throw new Error('Account creation failed: No user returned');
      }

      // Note: Students don't get profiles - they use the students table directly
      // The auth_user_id in the students table will link to this auth user

      return {
        success: true,
        authUserId: authData.user.id,
        password: password,
        accountType: 'student',
        emailConfirmed: true,
        message: `Student login account created!\nEmail: ${studentData.email}\nPassword: ${password}`
      };

    } catch (error) {
      console.error('Student account creation failed:', error);
      return { success: false, error: error.message };
    }
  };

  const createStudentAccount = async (studentData, parentData, existingParentId = null) => {
    if (!canManageOrganization() && !isParent()) {
      throw new Error('Unauthorized to create student accounts');
    }

    // Helper function to clean date and optional fields
    const cleanField = (value) => {
      if (!value || value === '' || value === undefined) return null;
      return value;
    };

    try {
      let parentId = null;
      let parentAccountInfo = null;

      // Step 1: Handle parent creation/selection
      if (parentData) {
        // Check if parent already exists
        const { data: existingParent } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('email', parentData.email)
          .eq('organization_id', organization.id)
          .eq('role', 'parent')
          .single();

        if (existingParent) {
          parentId = existingParent.id;
        } else {
          // Create new parent account with placeholder profile
          const parentResult = await createParentAccount(parentData, organization.id);

          if (!parentResult.success) {
            throw new Error(`Failed to create parent account: ${parentResult.error}`);
          }

          parentId = parentResult.profile.id;
          parentAccountInfo = {
            email: parentData.email,
            password: parentResult.password
          };
        }
      } else if (existingParentId) {
        // Use provided existing parent ID
        parentId = existingParentId;
      } else if (isParent()) {
        parentId = profile.id;
      } else {
        throw new Error('Parent information is required');
      }

      // Step 2: Create student record
      const studentRecord = {
        organization_id: organization.id,
        parent_id: parentId,
        full_name: studentData.full_name,
        first_name: studentData.first_name,
        last_name: studentData.last_name,
        date_of_birth: cleanField(studentData.date_of_birth),
        email: studentData.has_login_account ? studentData.email : null,
        phone: studentData.phone,
        address: studentData.address,
        emergency_contact_name: studentData.emergency_contact_name,
        emergency_contact_phone: studentData.emergency_contact_phone,
        belt_level: studentData.belt_level || 'white',
        status: 'active',
        has_login_account: studentData.has_login_account || false,
        notes: studentData.notes,
        enrollment_start_date: cleanField(studentData.enrollment_start_date),
        enrollment_end_date: cleanField(studentData.enrollment_end_date),
        height_cm: cleanField(studentData.height_cm),
        weight_kg: cleanField(studentData.weight_kg),
        gender: cleanField(studentData.gender),
        competition_eligible: studentData.competition_eligible || false,
        medical_clearance: studentData.medical_clearance || false,
        medical_conditions_list: cleanField(studentData.medical_conditions_list),
        additional_medical_notes: cleanField(studentData.additional_medical_notes),
        medical_info_updated_at: cleanField(studentData.medical_info_updated_at),
        avatar_url: cleanField(studentData.avatar_url)
      };

      // Step 3: If creating login account for student, create it
      let studentAccountInfo = null;
      if (studentData.has_login_account && studentData.email) {
        const studentResult = await createStudentLoginAccount(studentData, organization.id, parentId);

        if (studentResult.success) {
          studentRecord.auth_user_id = studentResult.authUserId;
          studentAccountInfo = {
            email: studentData.email,
            password: studentResult.password
          };
        } else {
          console.warn('Student login account creation failed, creating without login:', studentResult.error);
          studentRecord.has_login_account = false;
          studentRecord.email = null;
        }
      }

      const { data: newStudent, error: studentError } = await supabase
        .from('students')
        .insert(studentRecord)
        .select()
        .single();

      if (studentError) throw studentError;

      // Prepare success message with account details
      let message = `Student "${studentData.full_name}" created successfully!`;

      if (parentAccountInfo) {
        message += `\n\n🎫 PARENT REGISTRATION READY:\nEmail: ${parentAccountInfo.email}\nPassword: ${parentAccountInfo.password}\n\n📝 The parent needs to register at your app's signup page using these exact credentials to activate their account.`;
      }

      if (studentAccountInfo) {
        message += `\n\n🎫 STUDENT REGISTRATION READY:\nEmail: ${studentAccountInfo.email}\nPassword: ${studentAccountInfo.password}\n\n📝 The student needs to register at your app's signup page using these exact credentials to activate their account.`;
      } else if (studentData.has_login_account) {
        message += `\n\n⚠️ Note: Student login account could not be created`;
      }

      return {
        student: newStudent,
        parentAccount: parentAccountInfo,
        studentAccount: studentAccountInfo,
        message: message
      };

    } catch (error) {
      console.error('Error creating student account:', error);
      throw error;
    }
  };

  const canAccessCenter = (centerId) => {
    if (isSuperAdmin() || isOrganizationOwner()) return true;
    if (isCenterManager() && profile.center_id === centerId) return true;
    return false;
  };

  const canManageOrganization = () => {
    return isSuperAdmin() || isOrganizationOwner();
  };

  const hasFeature = (featureName) => {
    return organization?.features?.[featureName] === true;
  };

  // New feature access management functions
  const isFeatureEnabled = (featureName) => {
    // Super admins bypass all feature restrictions
    if (profile?.role === 'super_admin') return true;
    
    // If features not loaded yet, default to enabled to prevent blocking
    if (!organizationFeatures) return true;
    
    // Check if feature is enabled
    return organizationFeatures[featureName] === true;
  };

  const refreshOrganizationFeatures = async () => {
    if (!organization?.id) return;
    
    try {
      const features = await OrganizationFeatureService.getOrganizationFeatures(organization.id);
      setOrganizationFeatures(features);
      
      // Update cache
      const cachedAuth = authCache.get();
      if (cachedAuth) {
        authCache.set({
          ...cachedAuth,
          organizationFeatures: features
        });
      }
    } catch (error) {
      console.error('Error refreshing organization features:', error);
    }
  };

  const isTrialExpired = () => {
    if (!organization?.trial_ends_at) return false;
    return new Date(organization.trial_ends_at) < new Date();
  };

  // Create instructor profile and invitation
  // Create instructor account (no invitation needed)
  const createCoachAccount = async (coachData, organizationId) => {
    try {
      const password = generateSimplePassword(coachData.full_name);

      // Just return the generated password - no invitation system needed
      return {
        success: true,
        password: password,
        accountType: 'coach'
      };
    } catch (error) {
      console.error('Error creating coach account:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Create center manager account (no invitation needed)
  const createCenterManagerAccount = async (managerData, organizationId) => {
    try {
      const password = generateSimplePassword(managerData.full_name);

      // Just return the generated password - no invitation system needed
      return {
        success: true,
        password: password,
        accountType: 'center_manager'
      };
    } catch (error) {
      console.error('Error creating center manager account:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };


  const formatAccountMessage = (message) => {
    return message.split('\n').map((line, index) => {
      if (line.includes('🔑')) {
        return <div key={index} className="font-semibold text-blue-600">{line}</div>;
      } else if (line.includes('⚠️')) {
        return <div key={index} className="font-semibold text-amber-600">{line}</div>;
      } else if (line.includes('Email:') || line.includes('Password:')) {
        return <div key={index} className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{line}</div>;
      } else {
        return <div key={index}>{line}</div>;
      }
    });
  };

  // Background refresh function to silently update cached data
  const refreshAuthDataInBackground = async () => {
    try {

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        return;
      }

      // Fetch fresh profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          organization:organizations(*)
        `)
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        return;
      }

      // Update cache with fresh data
      const authData = {
        user: session.user,
        profile: profileData,
        organization: profileData.organization
      };

      authCache.set(authData);

      // Silently update state without loading indicators
      setUser(session.user);
      setProfile(profileData);
      setOrganization(profileData.organization);
      setUsingCachedData(false);

    } catch (error) {
      // Silent failure - don't log errors in production
    }
  };

  // Cache management functions
  const refreshUserData = async (forceRefresh = false) => {
    if (!user) return;

    if (forceRefresh) {
      // Clear cache and fetch fresh data
      authCache.clear();
      await fetchUserData(user.id);
    } else {
      // Use cache if available, otherwise fetch
      const cachedAuth = authCache.get();
      if (cachedAuth && cachedAuth.user?.id === user.id) {
        setProfile(cachedAuth.profile);
        setOrganization(cachedAuth.organization);
        setOrganizationFeatures(cachedAuth.organizationFeatures);
        setUsingCachedData(true);
      } else {
        await fetchUserData(user.id);
      }
    }
  };

  const invalidateCache = () => {
    authCache.clear();
    setUsingCachedData(false);
  };

  const updateCachedProfile = (updatedProfile) => {
    setProfile(updatedProfile);
    // Update cache with new profile data
    const cachedAuth = authCache.get();
    if (cachedAuth) {
      authCache.set({
        ...cachedAuth,
        profile: updatedProfile
      });
    }
  };

  const updateCachedOrganization = (updatedOrganization) => {
    setOrganization(updatedOrganization);
    // Update cache with new organization data
    const cachedAuth = authCache.get();
    if (cachedAuth) {
      authCache.set({
        ...cachedAuth,
        organization: updatedOrganization
      });
    }
  };

  const value = {
    user,
    profile,
    organization,
    organizationFeatures,
    loading,
    authInitialized,
    usingCachedData,
    signUp,
    signIn,
    signOut,
    resetPassword,
    createOrganization,
    joinOrganization,
    acceptInvitation,
    updateProfile,
    updateOrganization,
    refreshOrganization,
    fixParentOrganization,
    // Super Admin functions
    createOrganizationAsSuper,
    getAllOrganizations,
    updateOrganizationAsSuper,
    deleteOrganization,
    impersonateOrganization,
    exitImpersonation,
    // Role checks
    isSuperAdmin,
    isOrganizationOwner,
    isCenterManager,
    isCoach,
    isParent,
    isStudent,
    canAccessCenter,
    canManageOrganization,
    hasFeature,
    isTrialExpired,
    // Feature access management
    isFeatureEnabled,
    refreshOrganizationFeatures,
    // New parent-student system functions
    getStudentData,
    getParentChildren,
    canViewStudent,
    canEditStudent,
    createStudentAccount,
    createParentAccount,
    createCoachAccount,
    createCenterManagerAccount,
    createStudentLoginAccount,
    generateSimplePassword,
    formatAccountMessage,
    // Cache management
    refreshUserData,
    invalidateCache,
    updateCachedProfile,
    updateCachedOrganization,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};